import _ from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VisitorListItem } from "src/api/visitor/visitor-types";
import HistoryInfiniteScrollFooter from "src/pages/mypage/contract/components/HistoryInfiniteScrollFooter";
import Spinner from "src/components/Spinner";
import { numberToStringWithComma } from "src/utils/common-util";
import { createVisitorGroups } from "../functions/visitorHistory";
import type { VisitorHistoryPage } from "../types/visitorHistory";
import VisitorHistoryListItem from "./VisitorHistoryListItem";

type VisitorHistoryListProps = {
  visitorLogs?: VisitorListItem[];
  isLoading: boolean;
  isLoadingNextPage: boolean;
  hasNextPage: boolean;
  page: VisitorHistoryPage;
  onLoadNextPage: () => void;
};

const VisitorHistoryList = (props: VisitorHistoryListProps) => {
  const { visitorLogs, isLoading, isLoadingNextPage, hasNextPage, page, onLoadNextPage } = props;
  const visitorGroups = useMemo(() => createVisitorGroups(visitorLogs), [visitorLogs]);

  const stateOfData = useMemo(() => {
    if (isLoading) return "LOADING";
    if (!visitorLogs) return "NO_DATA";
    if (_.isEmpty(visitorLogs)) return "EMPTY";

    return "HAS_DATA";
  }, [isLoading, visitorLogs]);

  return (
    <div className={`history-list-container ${stateOfData === "HAS_DATA" ? "has-data" : ""}`}>
      {stateOfData === "LOADING" && (
        <div className="spinner-container">
          <Spinner size="page" />
        </div>
      )}
      {["NO_DATA", "EMPTY"].includes(stateOfData) && <ListEmpty />}
      {stateOfData === "HAS_DATA" && (
        <div className="history-list-content">
          <ListTotalCount totalCount={page.totalCount} />

          <ul className="visitor-history-list">
            {visitorGroups.map((group) => (
              <VisitorHistoryListItem key={group.key} group={group} />
            ))}
          </ul>
          <HistoryInfiniteScrollFooter //
            hasNextPage={hasNextPage}
            isLoadingNextPage={isLoadingNextPage}
            disabled={isLoading}
            onLoadNextPage={onLoadNextPage}
          />
        </div>
      )}
    </div>
  );
};

type ListTotalCountProps = {
  totalCount: number;
};

const ListTotalCount = (props: ListTotalCountProps) => {
  const { totalCount } = props;
  const { t } = useTranslation("visitor");

  return (
    <div className="visitor-history-total-count">
      <p>{t("totalCount", { value: numberToStringWithComma(totalCount) })}</p>
    </div>
  );
};

const ListEmpty = () => {
  const { t } = useTranslation("visitor");

  return (
    <div className="visitor-history-empty">
      <p>{t("noVisitorLogs")}</p>
    </div>
  );
};

export default VisitorHistoryList;
